import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Loader2, CheckCircle2, AlertCircle, CreditCard, Ban, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  active: { txt: "Attivo", cls: "bg-emerald-100 text-emerald-800" },
  trial: { txt: "In prova", cls: "bg-blue-100 text-blue-800" },
  admin_gift: { txt: "Omaggio (admin)", cls: "bg-violet-100 text-violet-800" },
  past_due: { txt: "Pagamento fallito", cls: "bg-amber-100 text-amber-800" },
  canceled: { txt: "Disdetto", cls: "bg-rose-100 text-rose-800" },
  expired: { txt: "Scaduto", cls: "bg-slate-200 text-slate-700" },
  unpaid: { txt: "Da pagare", cls: "bg-amber-100 text-amber-800" },
  pending: { txt: "In sospeso", cls: "bg-slate-100 text-slate-700" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Polling after Stripe redirect (?session_id=...)
  const sessionId = searchParams.get("session_id");

  const loadSub = async () => {
    try {
      const { data } = await api.get("/billing/my-subscription");
      setSub(data.subscription);
    } catch {
      /* silently ignore — empty state is shown */
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadSub();
      setLoading(false);
    })();
  }, []);

  // After redirect from Stripe — poll the checkout status
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) {
        toast.error("Verifica pagamento non conclusa. Aggiorna fra qualche minuto.");
        setSearchParams({}, { replace: true });
        return;
      }
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          toast.success("Pagamento ricevuto, abbonamento attivato!");
          await loadSub();
          setSearchParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Sessione di pagamento scaduta. Riprova.");
          setSearchParams({}, { replace: true });
          return;
        }
      } catch (e) {
        toast.error("Errore verifica pagamento");
      }
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, setSearchParams]);

  const cancel = async () => {
    if (!confirm("Confermi la disdetta? L'abbonamento resterà attivo fino alla scadenza del periodo già pagato.")) return;
    setCancelling(true);
    try {
      const { data } = await api.post("/billing/cancel-subscription");
      toast.success(data.message || "Abbonamento disdetto");
      await loadSub();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore disdetta");
    } finally {
      setCancelling(false);
    }
  };

  const reactivate = async () => {
    setReactivating(true);
    try {
      await api.post("/billing/reactivate-subscription");
      toast.success("Disdetta revocata. L'abbonamento continuerà.");
      await loadSub();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore revoca disdetta");
    } finally {
      setReactivating(false);
    }
  };

  if (loading) return <DashboardLayout role="consultant"><div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2456C6]" /></div></DashboardLayout>;

  const status = sub?.status || "none";
  const statusBadge = STATUS_LABEL[status] || { txt: status, cls: "bg-slate-100 text-slate-700" };
  const pendingCancel = sub?.cancel_at_period_end === true;

  return (
    <DashboardLayout role="consultant">
      <div className="p-4 sm:p-8 max-w-3xl mx-auto" data-testid="billing-page">
        <h1 className="text-3xl font-black text-[#1E293B] mb-6">Abbonamento</h1>

        {/* Current subscription card */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm" data-testid="current-sub-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Piano attuale</div>
              <div className="mt-1 text-2xl font-black text-[#1E293B]" data-testid="current-plan-name">
                {sub?.plan ? sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1) : "Nessun piano attivo"}
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {sub ? `Fatturazione ${sub.billing_cycle === "annual" ? "annuale" : "mensile"}` : "Attiva un piano per usare GoStreet"}
              </div>
            </div>
            {sub && (
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusBadge.cls}`} data-testid="sub-status-badge">
                {statusBadge.txt}
              </span>
            )}
          </div>

          {sub && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="px-3 py-2 rounded-lg bg-slate-50">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Prossimo rinnovo</div>
                <div className="font-bold text-[#1E293B] mt-0.5">{fmtDate(sub.current_period_end)}</div>
              </div>
              <div className="px-3 py-2 rounded-lg bg-slate-50">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Avvio abbonamento</div>
                <div className="font-bold text-[#1E293B] mt-0.5">{fmtDate(sub.started_at || sub.created_at)}</div>
              </div>
            </div>
          )}

          {/* Pending cancellation warning */}
          {pendingCancel && (
            <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="cancel-pending-banner">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-amber-900 text-sm">Disdetta pianificata</div>
                <div className="text-xs text-amber-800 mt-0.5">L&apos;abbonamento resterà attivo fino al <b>{fmtDate(sub.current_period_end)}</b>, poi sarà disattivato.</div>
              </div>
              <button
                onClick={reactivate}
                disabled={reactivating}
                data-testid="reactivate-sub-btn"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold disabled:opacity-60"
              >
                {reactivating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Annulla disdetta
              </button>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/pricing"
              data-testid="change-plan-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#2456C6] hover:bg-[#1B47AC] text-white font-bold text-sm"
            >
              <CreditCard className="w-4 h-4" />
              {sub ? "Cambia / aggiorna piano" : "Attiva un piano"}
            </Link>
            {sub && !pendingCancel && (
              <button
                onClick={cancel}
                disabled={cancelling}
                data-testid="cancel-sub-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 font-bold text-sm disabled:opacity-60"
              >
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                Disdici abbonamento
              </button>
            )}
          </div>

          <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">
            ✓ Disdetta 1-click come prevede l&apos;art. 65-bis Codice del Consumo. La disdetta è efficace alla scadenza del periodo già pagato, senza penali.
          </p>
        </div>

        {/* Compliance/help block */}
        <div className="mt-6 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-700">
          <h3 className="font-bold text-[#1E293B] mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Come funziona la fatturazione
          </h3>
          <ul className="space-y-1 list-disc pl-5">
            <li>Pagamento sicuro tramite Stripe (PCI-DSS Level 1, encryption end-to-end)</li>
            <li>30 giorni di prova gratuita al primo abbonamento</li>
            <li>Fattura elettronica SDI emessa automaticamente per ogni pagamento</li>
            <li>Disdici quando vuoi senza penali</li>
            <li>Email di promemoria 30 giorni prima del rinnovo annuale</li>
          </ul>
          <a href="mailto:info@totalklick.it?subject=Domanda fatturazione GoStreet"
             className="inline-flex items-center gap-1.5 text-[#2456C6] hover:underline mt-3 text-xs font-bold">
            <ExternalLink className="w-3 h-3" /> Hai domande? Scrivici
          </a>
        </div>
      </div>
    </DashboardLayout>
  );
}
